// src/pages/.../Hooks/useResults.ts
import { useCallback, useEffect, useMemo, useState } from 'react';
import { interviewRoundResults } from '../../../../../../../utils/api_service/SmartInterview';
import { sortResults } from '../../../../../../../utils/types/JobOpenings';

export interface IUser {
  firstName: string;
  lastName: string;
  email: string;
  timeZone: string | null;
  id: string;
}

export interface ITranscript {
  speaker: string;
  text: string;
}

export interface IQuestionAndAnswer {
  question: string;
  answer: string;
  score: number;
  justification: string;
}

export interface IScoreSection {
  score: number;
  justification: string;
}

export interface IAiOverallScore extends IScoreSection {}

export interface IInterviewResult {
  interviewRoundId: string;
  user: IUser;
  interviewStartTime: number;
  interviewType: string;
  interviewApplicantStatus: string;
  status: string;
  inPersonAddress: string | null;
  errorMessage: string | null;
  errorDetails: string | null;
  meetingLink: string | null;
  botId: string | null;
  virtualMeetingRecording: string | null;
  inPersonRecording: string | null;
  transcript: ITranscript[];
  summary: string[];
  questionAndAnswer: IQuestionAndAnswer[];
  averageScore: number;
  technicalSkills: IScoreSection;
  roleExperience: IScoreSection;
  industrySkills: IScoreSection;
  productKnowledge: IScoreSection;
  communication: IScoreSection;
  problemSolving: IScoreSection;
  leadership: IScoreSection;
  teamCollaboration: IScoreSection;
  suggestedQuestions: any[];
  aiOverallScore: IAiOverallScore;
  candidateSpeaker: string;
  panelistNotes: any[];
  jobApplicantId: string;
  id: string;
  applicantStatus?: string;
}

export interface IInterviewResultsResponse {
  results: IInterviewResult[];
}

interface IUseResultsProps {
  interviewRoundId?: string;
}

export const useResults = ({ interviewRoundId }: IUseResultsProps) => {
  // raw data + pagination state
  const [results, setResults] = useState<IInterviewResult[]>([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedCandidateIds, setSelectedCandidateIds] = useState<string[]>(
    [],
  );
  const [totalRecords, setTotalRecords] = useState<number>(0);

  const rowsPerPage = 10;

  // default sort: AI overall score descending
  const [sort, setSort] = useState<sortResults>({ aiOverallScore: 'desc' });
  const [isLoading, setIsLoading] = useState(false);

  const fetchResluts = useCallback(async () => {
    if (!interviewRoundId) return;
    setIsLoading(true);
    try {
      const resp = await interviewRoundResults(
        interviewRoundId,
        pageNumber,
        rowsPerPage,
      );

      const data = await resp.json();
      setResults(data.results);
      setTotalRecords(data.total_count);
      setTotalPages(Math.ceil(data.total_count / rowsPerPage));
      setSelectedCandidateResultIds(new Set());
    } catch (err) {
      setResults([]);
      setSelectedCandidateResultIds(new Set());
    } finally {
      setIsLoading(false);
    }
  }, [interviewRoundId, pageNumber, rowsPerPage]);
  const sortedResults = useMemo(() => {
    const entries = [...results];
    const [key, dir] = Object.entries(sort)[0] ?? [];
    if (!key) return entries;
    return entries.sort((a, b) => {
      const aVal = (a as any)[key];
      const bVal = (b as any)[key];
      // handle nested score objects, e.g. aiOverallScore.score
      const av =
        typeof aVal === 'object' && aVal !== null ? aVal.score ?? 0 : aVal;
      const bv =
        typeof bVal === 'object' && bVal !== null ? bVal.score ?? 0 : bVal;
      if (av < bv) return dir === 'asc' ? -1 : 1;
      if (av > bv) return dir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [results, sort]);

  useEffect(() => {
    fetchResluts();
  }, [fetchResluts]);

  // 4) Sorting handler (matches DataTableProps onSortChange signature)
  const handleSortChange = (key: string, dir: 'asc' | 'desc') => {
    setSort((prev) => {
      const current = prev[key as keyof sortResults];
      const nextDir = current === 'asc' ? 'desc' : 'asc';
      // Single‐sort: dump everything else
      return { [key]: nextDir } as sortResults;
    });
  };

  // 5) Row-click / detail-drawer state
  const [isOpenUserDetails, setIsOpenUserDetails] = useState(false);
  const [selectedResult, setSelectedResult] =
    useState<IInterviewResult | null>(null);
  const handleRowClick = (row: IInterviewResult) => {
    setSelectedResult(row);
    setIsOpenUserDetails(true);
  };
  const [selectedCandidateResultIds, setSelectedCandidateResultIds] = useState<
    Set<string>
  >(new Set());

  const handleSelectAll = () => {
    if (selectedCandidateResultIds.size === results.length) {
      // Deselect all
      setSelectedCandidateResultIds(new Set());
      setSelectedCandidateIds([]);
    } else {
      // Select all
      const allIds = new Set(results.map((result) => result.id));
      setSelectedCandidateResultIds(allIds);
      setSelectedCandidateIds(results.map((result) => result.id));
    }
  };

  const handleSelectRow = (rowIndex: number, row: IInterviewResult) => {
    const newSelection = new Set(selectedCandidateResultIds);
    if (newSelection.has(row.id)) {
      newSelection.delete(row.id);
    } else {
      newSelection.add(row.id);
    }
    setSelectedCandidateResultIds(newSelection);
    setSelectedCandidateIds([row.id]);
  };
  const isRowSelected = (rowIndex: number, row: IInterviewResult) => {
    return selectedCandidateResultIds.has(row.id);
  };
  const isAllSelected =
    selectedCandidateResultIds.size === results.length && results.length > 0;
  const isIndeterminate =
    selectedCandidateResultIds.size > 0 &&
    selectedCandidateResultIds.size < results.length;

  // 6) Search & filter stubs
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState<Record<string, any>>({});
  const handleSearch = (q: string) => setSearchQuery(q);
  const handleApplyFilters = (f: Record<string, any>) => setFilters(f);
  const onPageChange = (page: number) => setPageNumber(page);

  return {
    // table data + pagination
    results: sortedResults,
    pageNumber,
    totalPages,
    rowsPerPage,
    setPageNumber,
    isLoading,

    // sorting props for DataTable
    sortConfig: Object.entries(sort).map(([key, direction]) => ({
      key,
      direction,
    })),
    handleSortChange,

    // row-click / user details
    handleRowClick,

    handleSelectRow,
    handleSelectAll,
    isOpenUserDetails,
    setIsOpenUserDetails,
    selectedResult,
    selectedCandidateIds,

    // search & filters (if you wire them up later)
    searchQuery,
    handleSearch,
    filters,
    handleApplyFilters,
    totalRecords,
    isRowSelected,
    onPageChange,
    isAllSelected,
    isIndeterminate,
  };
};
